import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useAuth } from "../context/AuthContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { Customer } from "../types";
import { formatDate, formatDateTime } from "../utils/datetime";

/**
 * Top-level MSP-wide Licensing. Unifies every license source (Meraki
 * auto-pull + manually-entered) into one screen: KPI tiles, a filterable
 * expiry-sorted table across all customers, an on-demand "Import all", and a
 * manual-add form. Per-source detail still lives on each customer's Licenses
 * tab; this is the "everything I need to renew" cockpit.
 */

type LicenseRow = {
  source: "meraki" | "manual";
  id: number;
  customer_id: number;
  customer_name: string;
  vendor: string | null;
  product: string | null;
  reference: string | null;
  support_contact?: string | null;
  support_phone?: string | null;
  seat_count: number | null;
  annual_cost_cents: number | null;
  state: string | null;
  device_serial: string | null;
  expiration_date: string | null;
  days_remaining: number | null;
  severity: "ok" | "warning" | "critical" | "expired" | null;
  renewal_ticket_id: number | null;
  editable: boolean;
};

type Summary = {
  as_of: string;
  customers_with_licenses: number;
  total_tracked: number;
  expired: number;
  critical_7d_or_less: number;
  warning_30d: number;
  ok_beyond_30d: number;
  undated: number;
  meraki_count: number;
  manual_count: number;
};

const SEV_PILL: Record<string, string> = {
  ok: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  warning: "bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-300",
  critical: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  expired: "bg-red-200 text-red-900 font-bold dark:bg-red-900/50 dark:text-red-200",
};

const SEV_LABEL: Record<string, string> = {
  expired: "Expired",
  critical: "Critical — 7 days or less",
  warning: "Warning — 30 days",
  ok: "Beyond 30 days",
  undated: "No expiry date",
};

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return formatDate(iso);
}

function fmtDays(d: number | null): string {
  if (d === null) return "—";
  return d < 0 ? `${Math.abs(d)}d ago` : `${d}d`;
}

export default function Licensing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [rows, setRows] = useState<LicenseRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [withinDays, setWithinDays] = useState<number | "all">("all");
  const [source, setSource] = useState<"all" | "meraki" | "manual">("all");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [err, setErr] = useState("");
  const [notice, setNotice] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const licSort = useSort("expiration_date");

  const load = useCallback(async () => {
    setErr("");
    setLoading(true);
    try {
      const params: Record<string, string | number> = {};
      if (withinDays !== "all") params.within_days = withinDays;
      if (source !== "all") params.source = source;
      const [listRes, sumRes] = await Promise.all([
        api.get("/api/v1/licensing/all", { params }),
        api.get("/api/v1/licensing/summary"),
      ]);
      setRows(listRes.data || []);
      setSummary(sumRes.data || null);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load licensing data");
    } finally {
      setLoading(false);
    }
  }, [withinDays, source]);

  useEffect(() => {
    load();
  }, [load]);

  const importAll = async () => {
    if (
      !window.confirm(
        "Pull Meraki licenses for every customer with an active integration, then " +
          "run the renewal sweep (alerts, calendar reminders, and renewal tickets " +
          "for anything within 30 days)?\n\nThis may take a minute.",
      )
    )
      return;
    setImporting(true);
    setNotice("");
    setErr("");
    try {
      const r = await api.post("/api/v1/licensing/import-all");
      const m = r.data?.meraki || {};
      const s = r.data?.renewal_sweep || {};
      setNotice(
        `Import complete — Meraki: ${m.customers_synced ?? 0} synced, ` +
          `${m.customers_failed ?? 0} failed. Renewal sweep opened ${s.tickets_opened ?? 0} ` +
          `ticket(s), ${s.calendar_events ?? 0} calendar reminder(s).`,
      );
      await load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly - see the note in
  // utils/aiScreenContext: machine output and credential fields must
  // not reach a prompt.
  usePublishScreenData(
    "Licence renewals",
    useMemo(
      () =>
        rows.map((r: any) => ({
          customer: r.customer_name,
          product: r.product_name ?? r.name ?? null,
          source: r.source ?? null,
          quantity: r.quantity ?? null,
          renews: r.renewal_date ?? r.expires_at ?? null,
          days_until: r.days_until_renewal ?? null,
        })),
      [rows],
    ),
    undefined,
  );

  const grouped = useMemo(() => {
    const g: Record<string, LicenseRow[]> = {
      expired: [],
      critical: [],
      warning: [],
      ok: [],
      undated: [],
    };
    for (const r of rows) g[r.severity || "undated"]?.push(r);
    return g;
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Licensing</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
            Every license across all customers and sources — Meraki (pulled daily at 6:00 AM ET from
            each customer's integration) and manually-entered vendor licenses. Anything inside 30
            days gets an alert, a calendar reminder, and a renewal ticket automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isAdmin && (
            <>
              <button
                onClick={() => setShowAdd(true)}
                className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                + Add license
              </button>
              <button
                onClick={importAll}
                disabled={importing}
                className="text-sm bg-brand text-white rounded-lg px-3 py-1.5 hover:opacity-90 disabled:opacity-50"
              >
                {importing ? "Importing…" : "Import all now"}
              </button>
            </>
          )}
        </div>
      </div>

      {/* KPI tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Tile label="Tracked licenses" value={summary.total_tracked} />
          <Tile label="Customers" value={summary.customers_with_licenses} />
          <Tile
            label="Expired"
            value={summary.expired}
            accent={summary.expired > 0 ? "danger" : "ok"}
          />
          <Tile
            label="≤ 7 days"
            value={summary.critical_7d_or_less}
            accent={summary.critical_7d_or_less > 0 ? "danger" : "ok"}
          />
          <Tile
            label="≤ 30 days"
            value={summary.warning_30d}
            accent={summary.warning_30d > 0 ? "warn" : "ok"}
          />
          <Tile
            label="Meraki / Manual"
            value={`${summary.meraki_count} / ${summary.manual_count}`}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex items-end gap-3 flex-wrap">
        <label className="text-sm">
          <span className="block text-gray-600 dark:text-gray-300 mb-1">Expiring within</span>
          <select
            value={String(withinDays)}
            onChange={(e) =>
              setWithinDays(e.target.value === "all" ? "all" : Number(e.target.value))
            }
            className="border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="all">All licenses</option>
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-gray-600 dark:text-gray-300 mb-1">Source</span>
          <select
            value={source}
            onChange={(e) => setSource(e.target.value as "all" | "meraki" | "manual")}
            className="border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="all">All sources</option>
            <option value="meraki">Meraki</option>
            <option value="manual">Manual</option>
          </select>
        </label>
      </div>

      {notice && (
        <div className="bg-blue-50 border border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300 px-4 py-2 rounded-lg text-sm">
          {notice}
        </div>
      )}
      {err && (
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-8 text-center">
          <p className="text-sm text-gray-700 dark:text-gray-200 font-medium mb-1">
            No licenses{withinDays !== "all" ? ` expiring within ${withinDays} days` : ""}.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Add a Meraki integration under{" "}
            <Link to="/network-discovery" className="text-brand dark:text-brand-light underline">
              Network Discovery
            </Link>{" "}
            {isAdmin && "or use “+ Add license” to enter one manually."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {(["expired", "critical", "warning", "ok", "undated"] as const).map((sev) => {
            if (grouped[sev].length === 0) return null;
            return (
              <div
                key={sev}
                className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden"
              >
                <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {SEV_LABEL[sev]}
                  </h2>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${SEV_PILL[sev] || "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200"}`}
                  >
                    {grouped[sev].length}
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900 text-xs text-gray-500 dark:text-gray-400 uppercase">
                    <tr>
                      <SortableTh label="Customer" sortKey="customer_name" sort={licSort.sort} onSort={licSort.toggle} />
                      <SortableTh label="Vendor / product" sortKey="product" sort={licSort.sort} onSort={licSort.toggle} />
                      <SortableTh label="Source" sortKey="source" sort={licSort.sort} onSort={licSort.toggle} />
                      <SortableTh label="Expires" sortKey="expiration_date" sort={licSort.sort} onSort={licSort.toggle} />
                      <SortableTh label="Days" sortKey="days_remaining" sort={licSort.sort} onSort={licSort.toggle} />
                      <th className="text-left px-3 py-2 font-medium">Open</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {sortRows(grouped[sev], licSort.sort, (r, key) => {
                      switch (key) {
                        case "customer_name":
                          return (r.customer_name || "").toLowerCase();
                        case "product":
                          return (r.product || "").toLowerCase();
                        case "source":
                          return (r.source || "").toLowerCase();
                        case "days_remaining":
                          return r.days_remaining ?? 0;
                        default:
                          return r.expiration_date ? Date.parse(r.expiration_date) : 0;
                      }
                    }).map((r) => (
                      <tr
                        key={`${r.source}-${r.id}`}
                        className="text-gray-900 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-900"
                      >
                        <td className="px-3 py-2 font-medium">
                          <Link
                            to={`/customers/${r.customer_id}`}
                            className="text-brand dark:text-brand-light hover:underline"
                          >
                            {r.customer_name}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="font-medium">{r.product || "—"}</div>
                          <div className="text-[11px] text-gray-500 dark:text-gray-400">
                            {r.vendor}
                            {r.seat_count ? ` · ${r.seat_count} seats` : ""}
                            {r.device_serial ? ` · ${r.device_serial}` : ""}
                          </div>
                          {(r.support_contact || r.support_phone) && (
                            <div className="text-[11px] text-gray-400 dark:text-gray-500">
                              Support: {r.support_contact || "—"}
                              {r.support_phone ? ` · ${r.support_phone}` : ""}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <span className="inline-block px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 capitalize">
                            {r.source}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">{fmtDate(r.expiration_date)}</td>
                        <td className="px-3 py-2 text-xs">{fmtDays(r.days_remaining)}</td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap">
                          <Link
                            to={`/customers/${r.customer_id}?tab=meraki_licenses`}
                            className="text-brand dark:text-brand-light hover:underline mr-3"
                          >
                            Licenses
                          </Link>
                          {r.renewal_ticket_id && (
                            <Link
                              to={`/tickets/${r.renewal_ticket_id}`}
                              className="text-brand dark:text-brand-light hover:underline"
                            >
                              Ticket
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {summary && (
        <p className="text-[11px] text-gray-400 text-right">
          As of {formatDateTime(summary.as_of)}
        </p>
      )}

      {showAdd && (
        <AddLicenseModal
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  accent,
}: {
  label: string;
  value: number | string;
  accent?: "ok" | "warn" | "danger";
}) {
  const color =
    accent === "danger"
      ? "text-red-700 dark:text-red-300"
      : accent === "warn"
        ? "text-amber-700 dark:text-amber-300"
        : "text-gray-900 dark:text-gray-100";
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-3">
      <div className="text-[11px] text-gray-500 dark:text-gray-400">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 font-mono ${color}`}>{value}</div>
    </div>
  );
}

function AddLicenseModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customerId, setCustomerId] = useState<number | "">("");
  const [vendor, setVendor] = useState("");
  const [product, setProduct] = useState("");
  const [reference, setReference] = useState("");
  const [supportContact, setSupportContact] = useState("");
  const [supportPhone, setSupportPhone] = useState("");
  const [seatCount, setSeatCount] = useState("");
  const [annualCost, setAnnualCost] = useState("");
  const [expiration, setExpiration] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get("/api/v1/customers")
      .then((r) => setCustomers(r.data || []))
      .catch(() => setErr("Failed to load customers"));
  }, []);

  const save = async () => {
    if (!customerId || !vendor.trim() || !product.trim() || !expiration) {
      setErr("Customer, vendor, product, and expiration date are required.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      await api.post("/api/v1/licensing/manual", {
        customer_id: customerId,
        vendor: vendor.trim(),
        product: product.trim(),
        reference: reference.trim() || null,
        support_contact: supportContact.trim() || null,
        support_phone: supportPhone.trim() || null,
        seat_count: seatCount ? Number(seatCount) : null,
        annual_cost_cents: annualCost ? Math.round(Number(annualCost) * 100) : null,
        expiration_date: expiration,
        notes: notes.trim() || null,
      });
      onSaved();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to save license");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-5 space-y-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Add a manual license
        </h2>
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 px-3 py-2 rounded text-sm">
            {err}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Customer *</span>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : "")}
              className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
            >
              <option value="">Select a customer…</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <Field label="Vendor *" value={vendor} onChange={setVendor} placeholder="Microsoft" />
          <Field
            label="Product *"
            value={product}
            onChange={setProduct}
            placeholder="Microsoft 365 E3"
          />
          <Field
            label="Reference / PO"
            value={reference}
            onChange={setReference}
            placeholder="Optional"
          />
          <Field
            label="Support contact"
            value={supportContact}
            onChange={setSupportContact}
            placeholder="name / email / portal"
          />
          <Field
            label="Support phone"
            value={supportPhone}
            onChange={setSupportPhone}
            placeholder="Optional"
          />
          <Field label="Seats" value={seatCount} onChange={setSeatCount} type="number" />
          <Field
            label="Annual cost ($)"
            value={annualCost}
            onChange={setAnnualCost}
            type="number"
          />
          <label className="text-sm">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Expiration *</span>
            <input
              type="date"
              value={expiration}
              onChange={(e) => setExpiration(e.target.value)}
              className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
          <label className="col-span-2 text-sm">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
            />
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="text-sm bg-brand text-white rounded-lg px-4 py-1.5 hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save license"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <label className="text-sm">
      <span className="block text-gray-600 dark:text-gray-300 mb-1">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
      />
    </label>
  );
}
